import { Button, message } from 'antd';
import moment from 'moment';
import { useLocation, useParams } from 'react-router-dom';
import { useGetBusinessQuery, useGetDivisionQuery } from 'redux/services/abbyCadabby/abbyCadabbyApi';
import { useGetPriceBookEntriesQuery, useLazyGetPriceBookEntriesQuery } from 'redux/services/julia/juliaApi';
import { useAppSelector } from 'redux/store';
import * as xlsx from 'xlsx';

export const ExportPriceBookButton = (): JSX.Element => {
  const { id } = useParams();
  const location = useLocation();
  const { juliaPriceBookEntryParams } = useAppSelector((state) => state);

  const isDivisionContext = location.pathname.includes('division/');

  const { data: business } = useGetBusinessQuery(id as string, { skip: !id || isDivisionContext });
  const { data: division } = useGetDivisionQuery(id as string, { skip: !id || !isDivisionContext });

  const dataAreaId = isDivisionContext ? division?.business?.dataAreaId : business?.dataAreaId;
  const businessId = isDivisionContext ? division?.business?.id : business?.id;

  const { data: currentResponse } = useGetPriceBookEntriesQuery(
    { dataAreaId: dataAreaId || '', businessId: businessId || '', params: juliaPriceBookEntryParams },
    { skip: !dataAreaId || !businessId }
  );

  const [getLazyEntries, { isLoading }] = useLazyGetPriceBookEntriesQuery();

  const totalCount = currentResponse?.totalCount || 0;

  const handleExport = async (): Promise<void> => {
    if (!dataAreaId || !businessId) return;

    const { data: results } = await getLazyEntries({
      dataAreaId,
      businessId,
      params: { ...juliaPriceBookEntryParams, offset: 0, take: totalCount }
    }).unwrap();

    if (!results || results.length === 0) {
      message.error('No data to export');

      return;
    }

    const formattedResults = results.map((entry) => ({
      'Product Number': entry.productNumber,
      Condition: entry.condition,
      Type: entry.type,
      'List Price': entry.listPrice,
      'Unit Price': entry.unitPrice,
      'Discount %': entry.discountPercentage,
      'Effective Date': entry.effectiveDate,
      'End Date': entry.endDate || ''
    }));

    const worksheet = xlsx.utils.json_to_sheet(formattedResults);
    const workbook = xlsx.utils.book_new();

    xlsx.utils.book_append_sheet(workbook, worksheet, 'Price Book');
    const excelBuffer = await xlsx.write(workbook, { bookType: 'xlsx', bookSST: false, type: 'array' });
    const excelBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });

    const uri = window.URL.createObjectURL(excelBlob);
    const link = document.createElement('a');

    link.setAttribute('href', uri);
    link.setAttribute('download', `price-book-entries-${moment().format('YYYY-MM-DD')}.xlsx`);

    link.click();
    URL.revokeObjectURL(uri);
  };

  return (
    <Button onClick={handleExport} loading={isLoading} disabled={totalCount === 0}>
      Export
    </Button>
  );
};
